<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen pb-20">
    <div class="w-full mx-auto">
      <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <router-link
            to="/scorecard"
            class="text-sm text-blue-400 hover:text-blue-300 hover:underline transition-colors"
          >
            &larr; Back to Scorecard
          </router-link>
          <h1 class="text-2xl font-bold mt-2 text-gray-100">Recent Scorecards</h1>
          <p class="text-sm text-gray-400 mt-1">Submissions by submitted date for the selected month</p>
        </div>
      </div>

      <!-- Month Filter -->
      <div class="mb-6">
        <div class="flex items-center gap-4">
          <button
            @click="previousMonth"
            :disabled="loading"
            class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
          >
            &larr;
          </button>
          <div class="text-lg font-semibold text-gray-100 min-w-[200px] text-center">
            {{ currentMonthDisplay }}
          </div>
          <button
            @click="nextMonth"
            :disabled="loading"
            class="px-3 py-2 text-gray-300 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-warm-gray-800"
          >
            &rarr;
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label class="block text-xs text-gray-400 mb-1">Subject</label>
          <select
            v-model="filters.subjectId"
            class="w-full px-3 py-2 text-sm bg-warm-gray-800 border border-warm-gray-700 rounded text-gray-200"
          >
            <option value="">All subjects</option>
            <option
              v-for="subject in subjectsSorted"
              :key="subject.id"
              :value="subject.id"
            >
              {{ subject.data.name || subject.id }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-400 mb-1">Pass / Fail</label>
          <select
            v-model="filters.passFail"
            class="w-full px-3 py-2 text-sm bg-warm-gray-800 border border-warm-gray-700 rounded text-gray-200"
          >
            <option value="">All</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-400 mb-1">New / Opened</label>
          <select
            v-model="filters.openState"
            class="w-full px-3 py-2 text-sm bg-warm-gray-800 border border-warm-gray-700 rounded text-gray-200"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="opened">Opened</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-400 mb-1">Email</label>
          <input
            v-model.trim="filters.email"
            type="text"
            placeholder="Search email"
            class="w-full px-3 py-2 text-sm bg-warm-gray-800 border border-warm-gray-700 rounded text-gray-200 placeholder-gray-500"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="markAllVisibleOpened"
            :disabled="loading || openingBusy || visibleNewResultIds.length === 0"
            class="w-full px-3 py-2 text-sm bg-indigo-700 hover:bg-indigo-600 text-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-700"
          >
            Mark visible as opened
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <div v-else>
        <p class="text-sm text-gray-400 mb-3">
          Showing {{ filteredResults.length }} of {{ examResults.length }} submission{{ examResults.length !== 1 ? 's' : '' }}
        </p>

        <div v-if="filteredResults.length === 0" class="text-center py-12 text-gray-400">
          No submissions found for this month{{ hasActiveFilters ? ' with the current filters' : '' }}.
        </div>

        <div v-else class="bg-warm-gray-800 rounded-lg border border-warm-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-warm-gray-700 border-b border-warm-gray-600">
                  <th class="px-2 py-3 text-center w-10"></th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Subject</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Score</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Date Taken</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Submitted</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Comments</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Status</th>
                  <!-- <th class="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Actions</th> -->
                </tr>
              </thead>
              <tbody class="divide-y divide-warm-gray-700">
                <tr
                  v-for="result in filteredResults"
                  :key="result.id"
                  class="hover:bg-warm-gray-700/50 transition-colors"
                >
                  <td class="px-2 py-3 text-center">
                    <button
                      @click.stop="toggleOpened(result)"
                      :disabled="openingBusy"
                      class="p-1 text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50"
                      :title="openedResultIds.has(result.id) ? 'Mark as unopened' : 'Mark as opened'"
                    >
                      <svg
                        v-if="openedResultIds.has(result.id)"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5 text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 19l9-7 9 7M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
                        />
                      </svg>
                    </button>
                  </td>
                  <td class="px-4 py-3 text-gray-200">
                    <button
                      @click.stop="openUserSubmissionsModal(result)"
                      class="text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                    >
                      {{ result.data.email || 'N/A' }}
                    </button>
                  </td>
                  <td class="px-4 py-3 text-gray-300">
                    {{ getSubjectName(result.data.subjectId) || result.data.subjectId || 'N/A' }}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      :class="[
                        'font-semibold',
                        result.data.isPassed ? 'text-green-400' : 'text-red-400'
                      ]"
                    >
                      {{ result.data.score || 0 }}%
                    </span>
                  </td>
                  <td class="px-4 py-3 text-xs text-gray-400">
                    {{ formatExamDate(result.data.examDate) }}
                  </td>
                  <td class="px-4 py-3 text-xs text-gray-400">
                    {{ formatExamDate(result.data.submittedDate) }}
                  </td>
                  <td class="px-4 py-3 text-xs text-gray-400 max-w-xs whitespace-pre-wrap break-words">
                    <span v-if="hasComment(result.data.comments)">{{ result.data.comments }}</span>
                    <span v-else class="text-gray-500 italic">—</span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      :class="[
                        'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                        result.data.isPassed
                          ? 'bg-green-900 text-green-200'
                          : 'bg-red-900 text-red-200'
                      ]"
                    >
                      {{ result.data.isPassed ? '✓ Passed' : '✗ Failed' }}
                    </span>
                  </td>
                  <!--
                  <td class="px-4 py-3 text-center">
                    <button
                      @click.stop="openDetailsModal(result)"
                      class="px-3 py-1 text-xs rounded bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 transition-colors"
                    >
                      View Raw
                    </button>
                  </td>
                  -->
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div
      v-if="selectedResult"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeDetailsModal"
    >
      <div class="bg-warm-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold text-gray-100">Exam Result Details</h2>
          <button
            @click="closeDetailsModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div>
          <h3 class="text-sm font-semibold text-gray-300 mb-2">Raw Data</h3>
          <pre class="text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap bg-warm-gray-900 p-4 rounded border border-warm-gray-700">{{ JSON.stringify(selectedResult.data, null, 2) }}</pre>
        </div>
      </div>
    </div>

    <!-- User Submissions Modal -->
    <div
      v-if="selectedUserEmail"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-4"
      @click.self="closeUserSubmissionsModal"
    >
      <div class="bg-warm-gray-800 rounded-lg p-6 max-w-6xl w-full mx-4 my-4">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold text-gray-100">All Submissions for {{ selectedUserEmail }}</h2>
          <button
            @click="closeUserSubmissionsModal"
            class="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- KTRs Section -->
        <div class="mb-6 p-4 bg-warm-gray-900 rounded border border-warm-gray-700">
          <h3 class="text-sm font-semibold text-gray-300 mb-3">Knowledge Test Results (KTRs)</h3>

          <div v-if="loadingKtrs" class="flex items-center gap-2 text-sm text-gray-400">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading KTRs...</span>
          </div>

          <div v-else-if="selectedResultKtrs.length > 0" class="space-y-2">
            <a
              v-for="(ktr, index) in selectedResultKtrs"
              :key="index"
              :href="ktr.url"
              target="_blank"
              rel="noopener noreferrer"
              class="block px-3 py-2 bg-warm-gray-800 hover:bg-warm-gray-700 border border-warm-gray-600 rounded text-sm transition-colors break-all"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 flex-1 min-w-0">
                  <span class="mr-2">🔗</span>
                  <span class="text-blue-400 hover:text-blue-300">
                    <span v-if="ktr.filenameBeforeUpload">{{ ktr.filenameBeforeUpload }}</span>
                    <span v-else>{{ ktr.url }}</span>
                  </span>
                </div>
                <span v-if="ktr.uploadedAt" class="text-xs text-gray-500 ml-3 flex-shrink-0">
                  {{ formatKtrDate(ktr.uploadedAt) }}
                </span>
              </div>
            </a>
          </div>

          <p v-else class="text-sm text-gray-500 italic">No Knowledge Test Results available</p>
        </div>

        <div v-if="loadingUserSubmissions" class="flex justify-center items-center py-12">
          <svg
            class="animate-spin h-8 w-8 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <div v-else class="space-y-6">
          <div
            v-for="subject in sortedSubjects"
            :key="subject.id"
            class="bg-warm-gray-900 rounded-lg p-4 border border-warm-gray-700"
          >
            <h3 class="text-lg font-semibold text-gray-100 mb-3">
              {{ subject.data.name }}
            </h3>

            <div v-if="getSubmissionsForSubject(subject.id).length > 0">
              <p class="text-sm text-gray-400 mb-4">
                {{ getSubmissionsForSubject(subject.id).length }} submission{{ getSubmissionsForSubject(subject.id).length !== 1 ? 's' : '' }}
              </p>

              <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-warm-gray-800 border-b border-warm-gray-600">
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Dates</th>
                      <th class="px-4 py-2 text-center text-xs font-medium text-gray-300 uppercase">Score</th>
                      <th class="px-4 py-2 text-center text-xs font-medium text-gray-300 uppercase">Status</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Comments</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Subtopics</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-warm-gray-700">
                    <tr
                      v-for="submission in getSubmissionsForSubject(subject.id)"
                      :key="submission.id"
                      class="hover:bg-warm-gray-800/50 transition-colors"
                    >
                      <td class="px-4 py-2 text-gray-400 text-xs">
                        <div class="flex flex-col">
                          <span><strong>Exam:</strong> {{ formatExamDate(submission.data.examDate) }}</span>
                          <span><strong>Submitted:</strong> {{ formatExamDate(submission.data.submittedDate) }}</span>
                        </div>
                      </td>
                      <td class="px-4 py-2 text-center">
                        <span
                          :class="[
                            'font-semibold',
                            submission.data.isPassed ? 'text-green-400' : 'text-red-400'
                          ]"
                        >
                          {{ submission.data.score || 0 }}%
                        </span>
                      </td>
                      <td class="px-4 py-2 text-center">
                        <span
                          :class="[
                            'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                            submission.data.isPassed
                              ? 'bg-green-900 text-green-200'
                              : 'bg-red-900 text-red-200'
                          ]"
                        >
                          {{ submission.data.isPassed ? '✓ Passed' : '✗ Failed' }}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-left">
                        <span class="text-xs text-gray-400">
                          {{ submission.data.comments }}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-xs text-gray-400">
                        <div v-if="submission.data.subtopicVotes && submission.data.subtopicVotes.length > 0" class="space-y-1">
                          <div v-for="subtopicId in submission.data.subtopicVotes" :key="subtopicId" class="flex items-start">
                            <span class="mr-2">•</span>
                            <span>{{ getSubtopicName(subtopicId) || subtopicId }}</span>
                          </div>
                        </div>
                        <span v-else class="text-gray-500 italic">No subtopics</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-else class="text-sm text-gray-500 italic py-2">
              No submissions for this subject
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  getCaapSubjects,
  getCaapSubtopics,
  getExamResultsBySubmittedMonth,
  getAllExamResultsByUserId,
  getUserKtrs,
  getScorecardOpenedByMonth,
  openScorecardResult,
  unopenScorecardResult,
  openScorecardResults,
  formatScorecardYearMonth
} from './firebase'

const now = new Date()
const currentMonth = ref(now.getMonth())
const currentYear = ref(now.getFullYear())

const loading = ref(false)
const openingBusy = ref(false)
const subjects = ref([])
const subtopics = ref([])
const examResults = ref([])
const openedResultIds = ref(new Set())

const filters = ref({
  subjectId: '',
  passFail: '',
  openState: '',
  email: ''
})

const selectedResult = ref(null)
const selectedUserEmail = ref(null)
const selectedUserId = ref(null)
const userSubmissions = ref([])
const loadingUserSubmissions = ref(false)
const selectedResultKtrs = ref([])
const loadingKtrs = ref(false)

const currentMonthDisplay = computed(() => {
  return new Date(currentYear.value, currentMonth.value, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric'
  })
})

const subjectsSorted = computed(() => {
  return [...subjects.value].sort((a, b) => {
    const nameA = (a.data.name || a.id || '').toLowerCase()
    const nameB = (b.data.name || b.id || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
})

const sortedSubjects = computed(() => {
  return [...subjects.value].sort((a, b) => {
    const nameA = a.data.name || ''
    const nameB = b.data.name || ''
    return nameA.localeCompare(nameB)
  })
})

const hasActiveFilters = computed(() => {
  return Boolean(
    filters.value.subjectId ||
    filters.value.passFail ||
    filters.value.openState ||
    filters.value.email
  )
})

const filteredResults = computed(() => {
  return examResults.value.filter((result) => {
    const data = result.data || {}

    if (filters.value.subjectId && data.subjectId !== filters.value.subjectId) {
      return false
    }

    if (filters.value.passFail === 'passed' && data.isPassed !== true) {
      return false
    }
    if (filters.value.passFail === 'failed' && data.isPassed !== false) {
      return false
    }

    const isOpened = openedResultIds.value.has(result.id)
    if (filters.value.openState === 'new' && isOpened) {
      return false
    }
    if (filters.value.openState === 'opened' && !isOpened) {
      return false
    }

    if (filters.value.email) {
      const email = (data.email || '').toLowerCase()
      if (!email.includes(filters.value.email.toLowerCase())) {
        return false
      }
    }

    return true
  })
})

const visibleNewResultIds = computed(() => {
  return filteredResults.value
    .filter((result) => !openedResultIds.value.has(result.id))
    .map((result) => result.id)
})

const getSubjectName = (subjectId) => {
  const subject = subjects.value.find((s) => s.id === subjectId)
  return subject ? subject.data.name : subjectId || 'Unknown Subject'
}

const getSubtopicName = (subtopicId) => {
  const subtopic = subtopics.value.find((s) => s.id === subtopicId)
  return subtopic ? subtopic.data.name : null
}

const hasComment = (comments) => {
  return typeof comments === 'string' && comments.trim().length > 0
}

const formatExamDate = (examDate) => {
  if (!examDate || !examDate.seconds) return 'N/A'

  const date = new Date(examDate.seconds * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatKtrDate = (uploadedAt) => {
  if (!uploadedAt || !uploadedAt.seconds) return 'N/A'

  const date = new Date(uploadedAt.seconds * 1000)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getSubmissionsForSubject = (subjectId) => {
  return userSubmissions.value.filter((submission) => submission.data.subjectId === subjectId)
}

const yearMonthForResult = (result) => {
  const submittedDate = result?.data?.submittedDate
  if (submittedDate?.seconds) {
    const date = new Date(submittedDate.seconds * 1000)
    return formatScorecardYearMonth(date.getMonth(), date.getFullYear())
  }
  return formatScorecardYearMonth(currentMonth.value, currentYear.value)
}

const markResultOpened = async (result) => {
  if (!result?.id || openedResultIds.value.has(result.id)) return

  const yearMonth = yearMonthForResult(result)
  await openScorecardResult(result.id, yearMonth)
  openedResultIds.value.add(result.id)
  openedResultIds.value = new Set(openedResultIds.value)
}

const toggleOpened = async (result) => {
  if (!result?.id || openingBusy.value) return

  openingBusy.value = true
  try {
    if (openedResultIds.value.has(result.id)) {
      await unopenScorecardResult(result.id)
      openedResultIds.value.delete(result.id)
      openedResultIds.value = new Set(openedResultIds.value)
    } else {
      await markResultOpened(result)
    }
  } catch (error) {
    console.error('Error toggling opened state:', error)
  } finally {
    openingBusy.value = false
  }
}

const markAllVisibleOpened = async () => {
  const ids = visibleNewResultIds.value
  if (ids.length === 0 || openingBusy.value) return

  const yearMonth = formatScorecardYearMonth(currentMonth.value, currentYear.value)
  openingBusy.value = true
  try {
    await openScorecardResults(ids, yearMonth)
    ids.forEach((id) => openedResultIds.value.add(id))
    openedResultIds.value = new Set(openedResultIds.value)
  } catch (error) {
    console.error('Error marking visible results as opened:', error)
  } finally {
    openingBusy.value = false
  }
}

const openDetailsModal = async (result) => {
  selectedResult.value = result
  try {
    await markResultOpened(result)
  } catch (error) {
    console.error('Error marking result opened from View Raw:', error)
  }
}

const closeDetailsModal = () => {
  selectedResult.value = null
}

const openUserSubmissionsModal = async (result) => {
  const userId = result?.data?.userId
  const email = result?.data?.email
  if (!userId) return

  selectedUserId.value = userId
  selectedUserEmail.value = email
  userSubmissions.value = []
  selectedResultKtrs.value = []
  loadingUserSubmissions.value = true
  loadingKtrs.value = true

  try {
    await markResultOpened(result)
  } catch (error) {
    console.error('Error marking result opened from email click:', error)
  }

  try {
    const [allSubmissions, ktrs] = await Promise.all([
      getAllExamResultsByUserId(userId),
      getUserKtrs(userId)
    ])

    userSubmissions.value = allSubmissions
    selectedResultKtrs.value = ktrs
  } catch (error) {
    console.error('Error fetching user submissions:', error)
    userSubmissions.value = []
    selectedResultKtrs.value = []
  } finally {
    loadingUserSubmissions.value = false
    loadingKtrs.value = false
  }
}

const closeUserSubmissionsModal = () => {
  selectedUserEmail.value = null
  selectedUserId.value = null
  userSubmissions.value = []
  selectedResultKtrs.value = []
  loadingUserSubmissions.value = false
  loadingKtrs.value = false
}

const fetchPageData = async () => {
  loading.value = true
  try {
    const [subjectsData, subtopicsData, results, openedIds] = await Promise.all([
      getCaapSubjects(),
      getCaapSubtopics(),
      getExamResultsBySubmittedMonth(currentMonth.value, currentYear.value),
      getScorecardOpenedByMonth(currentMonth.value, currentYear.value).catch((error) => {
        console.error('Error fetching scorecard opened docs:', error)
        return []
      })
    ])

    subjects.value = subjectsData
    subtopics.value = subtopicsData
    examResults.value = results
    openedResultIds.value = new Set(openedIds)
  } catch (error) {
    console.error('Error fetching recent scorecards:', error)
    examResults.value = []
    openedResultIds.value = new Set()
  } finally {
    loading.value = false
  }
}

const previousMonth = () => {
  if (loading.value) return
  if (currentMonth.value === 0) {
    currentMonth.value = 11
    currentYear.value--
  } else {
    currentMonth.value--
  }
  fetchPageData()
}

const nextMonth = () => {
  if (loading.value) return
  if (currentMonth.value === 11) {
    currentMonth.value = 0
    currentYear.value++
  } else {
    currentMonth.value++
  }
  fetchPageData()
}

onMounted(() => {
  fetchPageData()
})
</script>
